import { useRef, useState } from 'react';
import {
  ConvertPaymentAmount,
  ConvertPaymentAmountRefActions,
} from 'src/components/MarketplacePayments/ConvertPaymentAmount/ConvertPaymentAmount';
import {
  DeleteMarketplacePayment,
  DeleteMarketplacePaymentRefActions,
} from 'src/components/MarketplacePayments/DeleteMarketplacePayment/DeleteMarketplacePayment';
import {
  EditMarketplacePayment,
  EditMarketplacePaymentRefActions,
} from 'src/components/MarketplacePayments/EditMarketplacePayment/EditMarketplacePayment';
import { useLockUnlockPaymentAction } from 'src/components/MarketplacePayments/hooks/useLockUnlockPaymentAction';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, MarketplaceSalePayment } from 'src/WebApiController';

interface MarketplacePaymentOptionsActionProps {
  marketplaceSalePayment: MarketplaceSalePayment;
}

export const MarketplacePaymentOptions = ({
  marketplaceSalePayment,
}: MarketplacePaymentOptionsActionProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const convertPaymentActionsRef =
    useRef<ConvertPaymentAmountRefActions | null>(null);
  const deletePaymentActionsRef =
    useRef<DeleteMarketplacePaymentRefActions | null>(null);
  const editPaymentActionsRef = useRef<EditMarketplacePaymentRefActions | null>(
    null
  );
  const lockUnlockPaymentAction = useLockUnlockPaymentAction(
    isLoading,
    setIsLoading,
    marketplaceSalePayment
  );

  const canConvertMarketplacePaymentLines = useUserHasFeature(
    Feature.MarketplacePaymentConversion
  );
  const canDeleteMarketplacePayment = useUserHasFeature(
    Feature.DeleteMarketplacePayment
  );
  const canEditMarketplacePayment = useUserHasFeature(
    Feature.EditMarketplacePayment
  );

  const canLockMarketplacePayment = useUserHasFeature(
    Feature.LockMarketplacePayment
  );

  const canShowOptions =
    canConvertMarketplacePaymentLines ||
    canDeleteMarketplacePayment ||
    canLockMarketplacePayment;

  if (!canShowOptions) {
    return null;
  }

  return (
    <>
      <PosDropdown
        key="event-action-dropdown"
        trigger={
          <div
            style={{ padding: `0 ${vars.spacing.sm} 0 ${vars.spacing.xxs}` }}
          >
            <MoreIcon
              withHoverEffect
              fill={IconsFill.textBrand}
              align="middle"
            />
          </div>
        }
        align="end"
      >
        {canLockMarketplacePayment && lockUnlockPaymentAction.dropdown}
        {canConvertMarketplacePaymentLines && (
          <PosDropdownItem
            key="convert-marketplace-payment"
            disabled={isLoading || !!marketplaceSalePayment.isLocked}
            onClick={() =>
              convertPaymentActionsRef.current?.showConvertPaymentAmountDialog()
            }
          >
            <Content id={ContentId.ConvertPayment} />
          </PosDropdownItem>
        )}
        {canDeleteMarketplacePayment && (
          <PosDropdownItem
            key="delete-marketplace-payment"
            disabled={isLoading || !!marketplaceSalePayment.isLocked}
            onClick={() =>
              deletePaymentActionsRef.current?.showDeleteMarketplacePaymentDialog()
            }
          >
            <Content id={ContentId.DeletePayment} />
          </PosDropdownItem>
        )}
        {canEditMarketplacePayment && (
          <PosDropdownItem
            key="edit-marketplace-payment"
            disabled={isLoading || !!marketplaceSalePayment.isLocked}
            onClick={() =>
              editPaymentActionsRef.current?.showEditMarketplacePaymentDialog()
            }
          >
            <Content id={ContentId.EditPayment} />
          </PosDropdownItem>
        )}
      </PosDropdown>
      {canConvertMarketplacePaymentLines && (
        <ConvertPaymentAmount
          convertPaymentAmountActionsRef={convertPaymentActionsRef}
          marketplaceSalePayment={marketplaceSalePayment}
        />
      )}
      {canDeleteMarketplacePayment && (
        <DeleteMarketplacePayment
          deleteMarketplacePaymentActionsRef={deletePaymentActionsRef}
          marketplaceSalePayment={marketplaceSalePayment}
        />
      )}
      {canEditMarketplacePayment && (
        <EditMarketplacePayment
          editMarketplacePaymentActionsRef={editPaymentActionsRef}
          marketplaceSalePayment={marketplaceSalePayment}
        />
      )}
    </>
  );
};
